/**
 * The typed API client.
 *
 * Every path is root-relative: in dev Vite proxies /api and /media to the backend, and
 * in the built app they really are the same origin. So there is no base URL to
 * configure and no CORS to negotiate from here.
 *
 * Identification is the X-Studio-User header — the whole of "auth" in this POC. It is
 * trivially spoofable and is never treated as a security boundary; see app/auth.py.
 */

import type {
  ArchiveAsset,
  Concept,
  Job,
  Project,
  ProjectSummary,
  RenderSettings,
  User,
  VoiceItem,
} from './types';

const API = '/api/studio';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** The generation cap is a cost guardrail, and the UI says so specifically. */
  get isRateLimit(): boolean {
    return this.status === 429;
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  userId?: string | null;
}

/** The platform error envelope: {"error": {"code", "message", "details"}}. */
interface ErrorEnvelope {
  error?: {code?: string; message?: string};
  message?: string;
  status?: string;
}

async function request<T>(path: string, {method = 'GET', body, userId}: Options = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (userId) headers['X-Studio-User'] = userId;

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const res = await fetch(path, {method, headers, body: payload});

  if (res.status === 204) return undefined as T;

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // No body — 204s are handled above, so anything else here is a genuine surprise.
  }

  const envelope = (data ?? {}) as ErrorEnvelope;

  if (!res.ok) {
    throw new ApiError(
      envelope.error?.message ?? envelope.message ?? `خطأ في الخدمة (${res.status})`,
      res.status,
      envelope.error?.code ?? '',
    );
  }

  // Several routes answer 200 with {"status": "error", "message": "..."}. Surfacing
  // that as success is how a reported failure turns into a silently broken screen, so
  // it is raised like any other error.
  if (envelope.status === 'error') {
    throw new ApiError(envelope.message ?? 'فشلت العملية', res.status, 'operation_failed');
  }

  return data as T;
}

/* --- users --------------------------------------------------------------------- */

export const createUser = (displayName: string) =>
  request<User>(`${API}/users`, {method: 'POST', body: {display_name: displayName}});

/* --- projects ------------------------------------------------------------------ */

export const listProjects = (userId: string, limit = 12) =>
  request<ProjectSummary[]>(`${API}/projects?limit=${limit}`, {userId});

export const getProject = (userId: string, id: string) =>
  request<Project>(`${API}/projects/${id}`, {userId});

export interface CreateProjectInput {
  input_payload: string;
  aspect_ratio: string;
  target_duration_s: number;
}

export const createProject = (userId: string, input: CreateProjectInput) =>
  request<Project>(`${API}/projects`, {
    method: 'POST',
    userId,
    body: {input_kind: 'brief', language: 'ar', ...input},
  });

export const patchProject = (userId: string, id: string, body: {aspect_ratio?: string}) =>
  request<Project>(`${API}/projects/${id}`, {method: 'PATCH', userId, body});

export const startGeneration = (userId: string, id: string) =>
  request<{status: string}>(`${API}/projects/${id}/generate`, {method: 'POST', userId});

export const listJobs = (userId: string, id: string) =>
  request<Job[]>(`${API}/projects/${id}/jobs`, {userId});

/* --- concept ------------------------------------------------------------------- */

export const patchConcept = (userId: string, id: string, body: Partial<Concept>) =>
  request<{status: string; changed: string[]}>(`${API}/projects/${id}/concept`, {
    method: 'PATCH',
    userId,
    body,
  });

/** Omit `field` to re-roll the whole concept; pass one to leave the other three alone. */
export const regenerateConcept = (userId: string, id: string, field?: string) =>
  request<{status: string}>(
    `${API}/projects/${id}/regenerate/concept${field ? `?field=${encodeURIComponent(field)}` : ''}`,
    {method: 'POST', userId},
  );

/* --- scenes -------------------------------------------------------------------- */

export const patchScene = (
  userId: string,
  id: string,
  sceneId: string,
  body: Record<string, string | number>,
) =>
  request<{status: string; changed: string[]}>(`${API}/projects/${id}/scenes/${sceneId}`, {
    method: 'PATCH',
    userId,
    body,
  });

export const regenerateScene = (userId: string, id: string, sceneId: string) =>
  request<{status: string}>(`${API}/projects/${id}/scenes/${sceneId}/regenerate`, {
    method: 'POST',
    userId,
  });

export const deleteScene = (userId: string, id: string, sceneId: string) =>
  request<{status: string; remaining: number}>(`${API}/projects/${id}/scenes/${sceneId}`, {
    method: 'DELETE',
    userId,
  });

/* --- visuals ------------------------------------------------------------------- */

export const generateSceneVisual = (userId: string, id: string, sceneId: string) =>
  request<{status: string}>(
    `${API}/projects/${id}/scenes/${sceneId}/generate/visual?prefer_video=true`,
    {method: 'POST', userId},
  );

export const assignArchiveVisual = (userId: string, id: string, sceneId: string) =>
  request<{status: string}>(`${API}/projects/${id}/scenes/${sceneId}/visual/from-archive`, {
    method: 'POST',
    userId,
  });

export const selectAlternative = (userId: string, id: string, sceneId: string, index: number) =>
  request<{status: string; preview_url: string}>(
    `${API}/projects/${id}/scenes/${sceneId}/visual/select?index=${index}`,
    {method: 'POST', userId},
  );

export const uploadSceneVisual = (userId: string, id: string, sceneId: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return request<{status: string; url: string}>(
    `${API}/projects/${id}/scenes/${sceneId}/upload/visual`,
    {method: 'POST', userId, body: form},
  );
};

export const generateAllVisuals = (userId: string, id: string) =>
  request<{status: string}>(`${API}/projects/${id}/generate/visuals?prefer_video=true`, {
    method: 'POST',
    userId,
  });

export const matchAllFromArchive = (userId: string, id: string) =>
  request<{status: string; assigned: number}>(`${API}/projects/${id}/generate/visuals/archive`, {
    method: 'POST',
    userId,
  });

/* --- voice-over ---------------------------------------------------------------- */

export const generateSceneVoiceover = (
  userId: string,
  id: string,
  sceneId: string,
  voice?: string,
  voiceId?: string,
) => {
  const params = new URLSearchParams();
  if (voiceId) params.set('voice_id', voiceId);
  else if (voice) params.set('voice', voice);
  const qs = params.toString();
  return request<{status: string}>(
    `${API}/projects/${id}/scenes/${sceneId}/generate/voiceover${qs ? `?${qs}` : ''}`,
    {method: 'POST', userId},
  );
};

export const generateAllVoiceovers = (userId: string, id: string) =>
  request<{status: string}>(`${API}/projects/${id}/generate/voiceover`, {method: 'POST', userId});

export const voiceoverPreview = (userId: string, id: string, sceneId: string) =>
  request<{status: string; audio_url: string; duration_s: number; voice: string}>(
    `${API}/projects/${id}/scenes/${sceneId}/voiceover/preview`,
    {userId},
  );

/* --- render settings ----------------------------------------------------------- */

export const saveRenderSettings = (userId: string, id: string, settings: Partial<RenderSettings>) =>
  request<{status: string; changed: string[]}>(`${API}/projects/${id}/render-settings`, {
    method: 'PATCH',
    userId,
    body: settings,
  });

export const uploadLogo = (userId: string, id: string, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return request<{status: string; logo_url: string; s3_key: string}>(
    `${API}/projects/${id}/render-settings/logo`,
    {method: 'POST', userId, body: form},
  );
};

/* --- render -------------------------------------------------------------------- */

export const renderVideo = (userId: string, id: string) =>
  request<{status: string; message: string; video_url: string}>(`${API}/projects/${id}/render`, {
    method: 'POST',
    userId,
  });

/* --- social media -------------------------------------------------------------- */

export const generateCaption = (userId: string, id: string) =>
  request<{status: string; caption: string; hashtags: string[]; full_text: string}>(
    `${API}/projects/${id}/social/caption`,
    {method: 'POST', userId},
  );

export const publishToSocial = (userId: string, id: string) =>
  request<{status: string; message: string; post_url: string; caption: string}>(
    `${API}/projects/${id}/social/publish`,
    {method: 'POST', userId},
  );

/* --- archive ------------------------------------------------------------------- */

export const listArchive = () =>
  request<{total: number; assets: ArchiveAsset[]}>(`${API}/archive`);

export const uploadArchiveAsset = (
  file: File,
  metadata: {description_ar: string; description_en: string; tags: string; duration_s: number},
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('description_ar', metadata.description_ar);
  form.append('description_en', metadata.description_en);
  form.append('tags', metadata.tags);
  form.append('duration_s', String(metadata.duration_s));
  return request<{status: string; filename: string; url: string; s3_key: string}>(
    `${API}/archive`,
    {method: 'POST', body: form},
  );
};

export const updateArchiveMetadata = (
  filename: string,
  body: {description_ar?: string; description_en?: string; tags?: string[]; duration_s?: number},
) =>
  request<{status: string; filename: string; asset: Record<string, unknown>}>(
    `${API}/archive/${encodeURIComponent(filename)}`,
    {method: 'PATCH', body},
  );

export const deleteArchiveAsset = (filename: string) =>
  request<{status: string; filename: string}>(
    `${API}/archive/${encodeURIComponent(filename)}`,
    {method: 'DELETE'},
  );

/* --- queue --------------------------------------------------------------------- */

/**
 * Nudge the in-process queue drainer.
 *
 * The worker container may or may not be running next to the API. Claiming is atomic
 * (`SELECT ... FOR UPDATE SKIP LOCKED`), so calling this when a worker is already on the
 * job simply finds nothing — it is a safe nudge, not a second execution path.
 */
export async function runJobs(_max = 2): Promise<number> {
  // The worker container handles all pipeline jobs. Nudging /internal/run-jobs
  // from the frontend caused session corruption on long-running LLM calls (trap #3)
  // and race conditions with the worker. The frontend now only polls job status.
  return 0;
}

/** Content-Length of a rendered file, for the export panel. Decoration, so failure is silent. */
export async function probeSize(url: string): Promise<number | null> {
  try {
    const res = await fetch(url, {method: 'HEAD'});
    const len = res.headers.get('content-length');
    return len ? Number(len) : null;
  } catch {
    return null;
  }
}

/* --- voice library ------------------------------------------------------------- */

export const listVoices = (userId: string) =>
  request<VoiceItem[]>(`${API}/voices`, {userId});

export const addVoice = (userId: string, body: {name: string; elevenlabs_voice_id: string; language?: string}) =>
  request<VoiceItem>(`${API}/voices`, {method: 'POST', userId, body});

export const cloneVoice = (userId: string, name: string, language: string, files: File[]) => {
  const form = new FormData();
  form.append('name', name);
  form.append('language', language);
  for (const f of files) form.append('files', f);
  return request<VoiceItem>(`${API}/voices/clone`, {method: 'POST', userId, body: form});
};

export const deleteVoice = (userId: string, voiceId: string) =>
  request<{status: string}>(`${API}/voices/${voiceId}`, {method: 'DELETE', userId});

export const renameVoice = (userId: string, voiceId: string, name: string) =>
  request<VoiceItem>(`${API}/voices/${voiceId}`, {method: 'PATCH', userId, body: {name}});

export const setDefaultVoice = (userId: string, voiceId: string) =>
  request<{status: string}>(`${API}/voices/${voiceId}/default`, {method: 'POST', userId});

export const clearDefaultVoice = (userId: string) =>
  request<{status: string}>(`${API}/voices/default`, {method: 'DELETE', userId});

export const previewVoice = (userId: string, voiceId: string) =>
  request<{voice_id: string; preview_url: string}>(`${API}/voices/${voiceId}/preview`, {userId});
